"use client";
import React from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Users,
  BookOpen,
  Award,
  Bell,
  Video,
  FileText,
  ClipboardList,
  Gift,
  Zap,
} from "lucide-react";

const TelegramChannelComponent: React.FC = () => {
  const features = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "متابعة أولية",
      description: "خطط متابعة وبداية التعلم",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "رسايل تشجيعية",
      description: "تحفيز مستمر ودعم معنوي",
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      title: "مسابقات واختبارات",
      description: "اختبر معلوماتك وتحدى نفسك",
      color: "from-emerald-500 to-teal-500",
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "جوايز ومكافات",
      description: "احصل على جوائز قيمة",
      color: "from-orange-500 to-red-500",
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "تنبيهات",
      description: "تذكيرات وتنبيهات مهمة",
      color: "from-indigo-500 to-purple-500",
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "فيديوهات وملخصات",
      description: "ملخصات مرئية سهلة الفهم",
      color: "from-pink-500 to-rose-500",
    },
  ];

  const stats = [
    { number: "10K+", label: "مشترك" },
    { number: "100+", label: "مسابقة" },
    { number: "10+", label: "كورس" },
  ];

  return (
    <div className="relative flex bg-gradient-to-br from-[#535953] via-[#333538] to-[#484a4d] rounded-3xl flex-col items-center justify-center p-8 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-3/4 right-1/2 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Telegram Badge */}
      <motion.button
        initial={{ opacity: 0, scale: 0, rotate: -45 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{
          duration: 0.8,
          delay: 0.2,
          type: "spring",
          stiffness: 200,
          damping: 15,
        }}
        className="absolute top-6 right-6 z-20 focus:outline-none"
        onClick={() => window.open("https://t.me/eduvalu2026", "_blank")}
      >
        <div className="relative">
          <div className="bg-gradient-to-r from-[#0088cc] to-[#229ED9] text-white px-4 py-2 rounded-full shadow-2xl border-2 border-white/20 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <span className="font-bold text-sm">Telegram</span>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0088cc] to-[#229ED9] rounded-full blur-lg opacity-50 animate-pulse"></div>
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
        </div>
      </motion.button>

      <div className="relative z-10 max-w-6xl mx-auto text-center">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <div className="flex justify-center mb-6">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="relative"
            >
              <div className="w-20 h-20 bg-gradient-to-r from-[#0088cc] to-[#229ED9] rounded-full flex items-center justify-center shadow-2xl">
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0088cc] to-[#229ED9] rounded-full blur-xl opacity-30 animate-pulse"></div>
            </motion.div>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            انضم إلى قناتنا على تليجرام
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            متابعة أولية، تحفيز دائم، ومسابقات ممتعة مع جوائز وتنبيهات وملخصات
            مرئية
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="grid grid-cols-3 gap-6 mb-12"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300"
            >
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                {stat.number}
              </div>
              <div className="text-gray-400 text-sm font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            ماذا ستحصل عليه؟
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className="relative group"
              >
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 group-hover:scale-105 h-full">
                  {/* Icon */}
                  <div className="relative mb-4 flex items-center justify-center">
                    <div
                      className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center text-white shadow-lg`}
                    >
                      {feature.icon}
                    </div>
                    <div
                      className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300`}
                    ></div>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-bold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Hover Glow Effect */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                  ></div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="space-y-6"
        >
          {/* <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Zap className="w-6 h-6 text-yellow-400" />
              <span className="text-yellow-400 font-semibold">عرض محدود</span>
            </div>
            <p className="text-gray-300 text-lg">
              أول 1000 مشترك سيحصلون على كورس مجاني + شهادة معتمدة
            </p>
          </div> */}

          <motion.a
            href="https://t.me/eduvalu2026"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-[#0088cc] to-[#229ED9] rounded-full text-white font-bold text-xl shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300"
          >
            <MessageCircle className="w-6 h-6" />
            انضم للقناة الآن
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              ←
            </motion.div>
          </motion.a>

          {/* <p className="text-gray-400 text-sm mt-4">
            مجاني 100% • بدون التزامات • يمكنك إلغاء الاشتراك في أي وقت
          </p> */}
        </motion.div>
      </div>
    </div>
  );
};

export default TelegramChannelComponent;
